import { SalesforceConnectionDetails } from '../../interfaces/salesforce/salesforce-connection-details';
import { DestinationTaskConfigBase } from '../config-objects/destination-service-config-base';
import { IfRuleGroup } from '../if-gate/if-rule-group';
import { SalesforceSortItem } from './salesforce-sort-item';

/**
 * Configuration for a SalesforceGetObjects task — queries Salesforce using SOQL and
 * returns matching records as flow payload records. Authenticates via OAuth client
 * credentials (clientId + clientSecret).
 */
export class SalesforceGetObjectsConfig
	extends DestinationTaskConfigBase
	implements SalesforceConnectionDetails
{
	/**
	 * Salesforce connected-app OAuth client ID used to authenticate.
	 * @default ''
	 */
	clientId = '';

	/**
	 * Salesforce connected-app OAuth client secret.
	 * @default ''
	 */
	clientSecret = '';

	/**
	 * Salesforce instance URL (e.g. https://myorg.salesforce.com).
	 * @default ''
	 */
	instanceURL = '';

	/**
	 * Salesforce API name of the object to query (e.g. 'Contact', 'Opportunity', 'Lead').
	 * @default ''
	 */
	salesforceObjectType = '';

	/**
	 * Array of Salesforce API field names to include in the SELECT clause.
	 * Leave empty to retrieve all fields via describeSObject (slower).
	 * @default []
	 */
	fields: string[] = [];

	/**
	 * SOQL WHERE conditions expressed as an IfRuleGroup.
	 * Only supports operators: ===, !==, >, <, >=, <=, ARRAY_CONTAINS (LIKE), IN, NOT IN.
	 * Only supports dataTypes: String (0), Number (1), Boolean (2).
	 * Leave ruleGroup.ruleGroup as an empty array for no WHERE clause.
	 */
	ruleGroup: IfRuleGroup = new IfRuleGroup();

	/**
	 * Array of ORDER BY clauses. Each item specifies a field and sort direction.
	 * @default []
	 */
	sort: SalesforceSortItem[] = [];

	/**
	 * Number of records to skip (OFFSET). Supports a static number or an expression string
	 * for dynamic pagination (e.g. a flow variable holding the current page offset).
	 * @default 0
	 */
	offset: number | string = 0;

	/**
	 * Maximum number of records to return (LIMIT). Supports a static number or an expression
	 * string. The `batchDone` variable is set to true when fewer records than `limit` are returned.
	 * @default 100
	 */
	limit: number | string = 100;

	/**
	 * Name of the flow variable set to `true` when the number of returned records is less than
	 * `limit` — indicating no more records remain. Read downstream (e.g. in a Loop) to detect
	 * end-of-stream.
	 * @default 'SalesforceBatchDone'
	 */
	batchDone: string = 'SalesforceBatchDone';

	constructor(init?: Partial<SalesforceGetObjectsConfig>) {
		super(init);
		if (init) {
			Object.assign(this, init);
		}
	}
}
